import { Lock, Plus } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The furniture every settings screen repeats.
 *
 * Each section is a list of records with a form beside it, and without this
 * the same card, empty state and read-only banner would be written a dozen
 * times over. Nothing here knows about a table -- it is layout only.
 */

export function SectionCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="bg-card text-card-foreground flex flex-col gap-4 rounded-lg border p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-y-0.5">
          <h3 className="text-sm font-medium">{title}</h3>
          {description && <p className="text-muted-foreground text-xs">{description}</p>}
        </div>

        {action}
      </div>

      {children}
    </section>
  );
}

/** Says why the controls are disabled, once, instead of on every field. */
export function ReadOnlyNotice({ permission }: { permission: string }) {
  return (
    <p className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm">
      <Lock className="size-4 shrink-0" />
      You can see these settings but not change them. That needs the &ldquo;{permission}&rdquo;
      permission.
    </p>
  );
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="text-muted-foreground flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center text-sm">
      <p className="max-w-sm text-balance">{children}</p>
      {action}
    </div>
  );
}

export function AddButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick} disabled={disabled}>
      <Plus className="size-4" />
      {children}
    </Button>
  );
}

export function SettingsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * A row in a settings list: name and detail on the left, controls on the
 * right, and the whole row clickable when it opens an editor.
 */
export function ListRow({
  title,
  subtitle,
  badges,
  actions,
  onClick,
  selected,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
}) {
  const content = (
    <>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{title}</span>
          {badges}
        </div>
        {subtitle && <p className="text-muted-foreground truncate text-xs">{subtitle}</p>}
      </div>

      {actions}
    </>
  );

  if (!onClick) {
    return <li className="flex items-center gap-3 px-4 py-3">{content}</li>;
  }

  return (
    <li className={selected ? 'bg-muted/50' : undefined}>
      {/*
        The row is a button and the actions sit outside it: nesting a delete
        control inside a clickable row makes every deletion also a navigation.
      */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={onClick} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{title}</span>
            {badges}
          </div>
          {subtitle && <p className="text-muted-foreground truncate text-xs">{subtitle}</p>}
        </button>

        {actions}
      </div>
    </li>
  );
}

export function ListShell({ children }: { children: ReactNode }) {
  return <ul className="divide-y rounded-lg border">{children}</ul>;
}
